import React from 'react'
import {
  CheckCircle2Icon,
  CircleDotIcon,
  ClockIcon,
  LightbulbIcon,
  OctagonPauseIcon,
  ThumbsDownIcon,
} from 'lucide-react'

import { useSnackbar } from 'contexts'
import { useInternalPromoteRequest, useInternalSetSupportStatus } from 'hooks/api'
import type { SupportRequest, SupportRequestStatus } from 'services/supportApi'

/**
 * The kebab actions on a support request, for BOTH internal surfaces.
 *
 * Lifted out of `InternalSupportPage` when the queue stopped expanding a request
 * inline and started linking to `/internal/support/:id`. Two surfaces now offer
 * the same set — the row in the queue and the header of the request's own page —
 * and the failure mode of duplicating it is specific rather than theoretical: a
 * status added to one list and not the other means a staffer can set a state from
 * the queue that they cannot set (or undo) from the page they were sent to.
 *
 * Same argument as `RequestThread` being shared with the customer side, one level
 * down (§36 — the second hand-roll is a shared component).
 */

/**
 * Every status a staffer can move a request to, each with the glyph for its own
 * verb rather than a shared dot (§21).
 */
export const REQUEST_STATUS_ACTIONS: {
  status: SupportRequestStatus
  label: string
  icon: React.ReactNode
}[] = [
  { status: 'open', label: 'Mark as open', icon: <CircleDotIcon strokeWidth={1} /> },
  { status: 'in_progress', label: 'Mark in progress', icon: <ClockIcon strokeWidth={1} /> },
  {
    status: 'waiting_on_customer',
    label: 'Waiting on customer',
    icon: <OctagonPauseIcon strokeWidth={1} />,
  },
  { status: 'resolved', label: 'Mark resolved', icon: <CheckCircle2Icon strokeWidth={1} /> },
  { status: 'closed', label: 'Close request', icon: <ThumbsDownIcon strokeWidth={1} /> },
]

/**
 * Builds the action list for one request. A hook because it owns the two
 * mutations and the error toast, which both call sites would otherwise wire
 * identically.
 */
export const useRequestActions = () => {
  const { open: notification } = useSnackbar()
  const { mutate: setStatus } = useInternalSetSupportStatus()
  const { mutate: promote } = useInternalPromoteRequest()

  const fail = (error: any, fallback: string) =>
    notification({ type: 'error', title: error?.message || fallback })

  return (request: SupportRequest) => [
    // The status the request is ALREADY in is dropped rather than shown disabled:
    // a menu of five where one is inert is a menu you have to read twice, and the
    // current status is on the chip beside the kebab anyway.
    ...REQUEST_STATUS_ACTIONS.filter(action => action.status !== request.status).map(action => ({
      label: action.label,
      icon: action.icon,
      onClick: () =>
        setStatus(
          { id: request.id, status: action.status },
          { onError: (e: any) => fail(e, "Couldn't change the status.") },
        ),
    })),
    {
      /*
        NAMED FOR WHERE IT LANDS, because that is the part a staffer can get wrong.

        The action files the request onto the SUBMITTING COMPANY's own board — it is
        company-scoped, and there is no global roadmap for it to reach (a brand sees
        only rows whose company_id is its own; see `listBoard`). "Promote to board"
        was accurate but said none of that: "promote" describes Vera's internal
        bookkeeping rather than the outcome, and the bare "board" is the one word in
        the sentence a reader would want qualified — a queue that spans every company
        is exactly where "which board?" is a real question.

        "Add to their board" answers it in the row itself. "Their" is unambiguous on
        this surface: the card names the company two lines below, so the antecedent is
        on screen. The alternatives were worse — "Add to the company board" reads as
        one canonical board shared by all companies, which is the precise misreading
        to avoid, and naming the brand inline ("Add to Louis Vuitton's board") makes
        the menu row length depend on the customer's name.

        The toast follows the label. A confirmation that says something different from
        the control that caused it is how a user learns to distrust both.
      */
      label: 'Add to their board',
      icon: <LightbulbIcon strokeWidth={1} />,
      divider: true,
      // Only a feature suggestion belongs on a feature board. A bug promoted to a
      // votable item would be asking a brand to vote on whether we fix a defect.
      visible: request.type === 'feature' && !request.boardItemId,
      onClick: () =>
        promote(
          { id: request.id },
          {
            onSuccess: () => notification({ type: 'success', title: 'Added to their board.' }),
            onError: (e: any) => fail(e, "Couldn't add this to their board."),
          },
        ),
    },
  ]
}
